import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  REVENUE_CHART_CARD_SHADOW,
  TOP_SERVICES_CARD_BORDER,
  TOP_SERVICES_CARD_GAP,
  TOP_SERVICES_CARD_PADDING_BOTTOM,
  TOP_SERVICES_CARD_PADDING_TOP,
  TOP_SERVICES_CARD_PADDING_X,
  TOP_SERVICES_CARD_RADIUS,
} from "../services/constants";
import {
  TEAM_TABLE_BODY_CELL_CLASS,
  TEAM_TABLE_HEADER_CELL_CLASS,
  TEAM_TABLE_ROW_HEIGHT,
} from "./constants";
import {
  TeamMetricCell,
  TeamRankCell,
  TeamStaffNameCell,
} from "./teamTableCells";
import type { TeamSegment, TeamStaffRow } from "./types";

const TABLE_BODY_ROW_STYLE = {
  height: TEAM_TABLE_ROW_HEIGHT,
  maxHeight: TEAM_TABLE_ROW_HEIGHT,
} as const;

const TABLE_BODY_CELL_STYLE = {
  ...TABLE_BODY_ROW_STYLE,
  boxSizing: "border-box" as const,
};

function bodyCellClass(isLastRow: boolean, extra?: string) {
  return cn(TEAM_TABLE_BODY_CELL_CLASS, isLastRow && "border-b-0", extra);
}

interface TeamStaffPerformanceTableProps {
  rows: TeamStaffRow[];
  segment: TeamSegment;
  embedded?: boolean;
  className?: string;
  onViewAll?: () => void;
  onRowClick?: (row: TeamStaffRow) => void;
}

export const TeamStaffPerformanceTable = memo(
  function TeamStaffPerformanceTable({
    rows,
    segment,
    embedded = false,
    className,
    onViewAll,
    onRowClick,
  }: TeamStaffPerformanceTableProps) {
    const { t } = useTranslation();
    const nameColumnLabel =
      segment === "teams"
        ? t("analytics.team.teamColumn", "Team")
        : t("analytics.common.name", "Name");
    const showExtendedColumns = segment !== "teams";

    return (
      <div
        className={cn(
          "flex w-full flex-col overflow-hidden font-sans",
          !embedded && "bg-white",
          !embedded && REVENUE_CHART_CARD_SHADOW,
          embedded && "border-0 bg-transparent p-0 shadow-none",
          className,
        )}
        style={
          embedded
            ? { maxWidth: "100%", gap: TOP_SERVICES_CARD_GAP }
            : {
                maxWidth: "100%",
                borderRadius: TOP_SERVICES_CARD_RADIUS,
                border: `1px solid ${TOP_SERVICES_CARD_BORDER}`,
                paddingTop: TOP_SERVICES_CARD_PADDING_TOP,
                paddingRight: TOP_SERVICES_CARD_PADDING_X,
                paddingBottom: TOP_SERVICES_CARD_PADDING_BOTTOM,
                paddingLeft: TOP_SERVICES_CARD_PADDING_X,
                gap: TOP_SERVICES_CARD_GAP,
              }
        }
      >
        {onViewAll ? (
          <div className="flex shrink-0 items-center justify-end gap-3">
            <Button
              type="button"
              variant="span"
              className="inline-flex h-auto items-center gap-0.5 p-0 text-xs font-medium text-[#171717] hover:bg-transparent"
              onClick={onViewAll}
            >
              {t("analytics.common.viewAll", "View all")}
              <ChevronRight className="size-3.5" />
            </Button>
          </div>
        ) : null}

        <div className="min-h-0 overflow-x-auto">
          <table
            className={cn(
              "w-full table-fixed border-collapse text-sm",
              showExtendedColumns ? "min-w-[640px]" : "min-w-[520px]",
            )}
          >
            <thead>
              <tr>
                <th className={cn(TEAM_TABLE_HEADER_CELL_CLASS, "w-12")}>
                  {t("analytics.common.rate", "Rate")}
                </th>
                <th
                  className={cn(TEAM_TABLE_HEADER_CELL_CLASS, "min-w-[140px]")}
                >
                  {nameColumnLabel}
                </th>
                <th className={TEAM_TABLE_HEADER_CELL_CLASS}>
                  {t("analytics.team.table.tasksCompleted", "Tasks completed")}
                </th>
                <th className={TEAM_TABLE_HEADER_CELL_CLASS}>
                  {t("analytics.team.table.tasksCancelled", "Tasks cancelled")}
                </th>
                <th className={TEAM_TABLE_HEADER_CELL_CLASS}>
                  {t("analytics.team.table.avgTimeOnTask", "Avg time on task")}
                </th>
                {showExtendedColumns ? (
                  <>
                    <th className={TEAM_TABLE_HEADER_CELL_CLASS}>
                      {t("analytics.team.table.delays", "Delays")}
                    </th>
                    <th className={TEAM_TABLE_HEADER_CELL_CLASS}>
                      {t("analytics.team.table.dayOff", "Day off")}
                    </th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const isLastRow = index === rows.length - 1;

                return (
                  <tr
                    key={row.id}
                    style={TABLE_BODY_ROW_STYLE}
                    className={
                      onRowClick
                        ? "cursor-pointer hover:bg-[#FAFAFA]"
                        : undefined
                    }
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    <td
                      className={bodyCellClass(isLastRow, "w-12")}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <TeamRankCell rank={row.rank} />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <TeamStaffNameCell row={row} />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <TeamMetricCell
                        value={row.tasksCompleted}
                        variant="positive"
                      />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <TeamMetricCell
                        value={row.tasksCancelled}
                        variant={row.tasksCancelled > 0 ? "warning" : "default"}
                      />
                    </td>
                    <td
                      className={bodyCellClass(isLastRow)}
                      style={TABLE_BODY_CELL_STYLE}
                    >
                      <TeamMetricCell value={row.avgTimeOnTask} />
                    </td>
                    {showExtendedColumns ? (
                      <>
                        <td
                          className={bodyCellClass(isLastRow)}
                          style={TABLE_BODY_CELL_STYLE}
                        >
                          <TeamMetricCell
                            value={row.delays}
                            variant={row.delays > 0 ? "warning" : "default"}
                          />
                        </td>
                        <td
                          className={bodyCellClass(isLastRow)}
                          style={TABLE_BODY_CELL_STYLE}
                        >
                          <TeamMetricCell value={row.daysOff} />
                        </td>
                      </>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);
